<template>
  <MarketplaceCard
    v-if="settings && config"
    v-model="config.gpi_enabled"
    :logo-path="imageUrl"
    logo-alt="Global Payments"
    description="Industry-leading payment solutions. Global Payments Integrated is a true partnership in every sense of the word."
    cy-selector="marketplaceGPI"
  >
    <template #configure>
      <div class="d-inline">
        <span class="link" @click="toggleModal('card')">Credit Card</span>
        <Divider vertical class="d-inline" />
        <span class="link" @click="toggleModal('ach')">ACH</span>
      </div>
    </template>
    <template #modal>
      <div>
        <Modal
          v-if="modalOpen"
          title="Set Up Information - Global Payments"
          class-name="gpi-config-modal"
          :disabled="!isValid"
          @submit="handleSave"
          @hide-modal="toggleModal"
        >
          <ButtonToggle
            v-model="modalMode"
            class="toggle-button mb-4"
            :options="modalModes"
          />
          <div
            v-if="modalMode === 'card'"
            id="card-fields"
            class="d-flex flex-column"
          >
            <TextField
              v-model="config.gpi_x_web_id"
              class="full-width"
              label="Card Web ID"
              name="gpi_x_web_id"
            />
            <TextField
              v-model="config.gpi_terminal_id"
              class="full-width"
              label="Card Terminal ID"
              name="gpi_terminal_id"
            />
            <TextField
              v-model="config.gpi_auth_key"
              class="full-width"
              label="Card Auth Key"
              name="gpi_auth_key"
            />
          </div>
          <div
            v-if="modalMode === 'ach'"
            id="ach-fields"
            class="d-flex flex-column"
          >
            <TextField
              v-model="config.gpi_ach_x_web_id"
              class="full-width"
              label="ACH Web ID"
              name="gpi_ach_x_web_id"
            />
            <TextField
              v-model="config.gpi_ach_terminal_id"
              class="full-width"
              label="ACH Terminal ID"
              name="gpi_ach_terminal_id"
            />
            <TextField
              v-model="config.gpi_ach_auth_key"
              class="full-width"
              label="ACH Auth Key"
              name="gpi_ach_auth_key"
            />
          </div>
          <div id="shared-fields" class="d-flex flex-column">
            <Checkbox
              v-model="config.gpi_card_savable"
              label="Allow customers to save their payment method"
              class="d-inline mt-2"
            />
          </div>
        </Modal>
      </div>
    </template>
  </MarketplaceCard>
</template>

<script>
import { mapActions, mapState } from "vuex";

import ButtonToggle from "@/components/ButtonToggle.vue";
import Checkbox from "@/components/Checkbox.vue";
import Divider from "@/components/Divider.vue";
import Modal from "@/components/Modal.vue";
import TextField from "@/components/TextField.vue";
import GlobalPaymentsLogo from "@/images/global_payments_logo.png";
import MarketplaceCard from "./MarketplaceCard.vue";

export default {
  components: {
    MarketplaceCard,
    Divider,
    Modal,
    TextField,
    Checkbox,
    ButtonToggle
  },
  data() {
    return {
      modalOpen: false,
      modalMode: "card",
      config: null
    };
  },
  computed: {
    ...mapState({
      settings: ({ paymentMethodsSettings }) =>
        paymentMethodsSettings ? paymentMethodsSettings.payment_methods : null
    }),
    modalModes: function () {
      return [
        { value: "card", label: "Credit Card" },
        { value: "ach", label: "ACH" }
      ];
    },
    isValid() {
      let matchFields = [];

      if (this.modalMode === "card") {
        const { gpi_x_web_id, gpi_terminal_id, gpi_auth_key } = this.config;

        matchFields = [gpi_x_web_id, gpi_terminal_id, gpi_auth_key];
      } else {
        const { gpi_ach_x_web_id, gpi_ach_terminal_id, gpi_ach_auth_key } =
          this.config;

        matchFields = [gpi_ach_x_web_id, gpi_ach_terminal_id, gpi_ach_auth_key];
      }

      // if we have any set
      if (matchFields.some((field) => Boolean(field))) {
        // then we must have all set
        return matchFields.every((field) => Boolean(field));
      }
      // but all empty is OK
      return true;
    },
    imageUrl: {
      get() {
        return GlobalPaymentsLogo;
      },
    },
  },
  watch: {
    settings(newValue, oldValue) {
      if (newValue !== oldValue && !this.config) {
        this.initForm();
      }
    },
    "config.gpi_enabled": function (newValue, oldValue) {
      if (newValue !== oldValue && oldValue !== undefined) {
        if (newValue !== this.settings.gpi_enabled) {
          this.handleCheckChange("gpi_enabled", newValue);
        }
      }
    },
    modalOpen(newValue, oldValue) {
      if (newValue !== oldValue && !newValue) {
        this.initForm();
      }
    }
  },
  mounted() {
    this.initForm();
  },
  methods: {
    ...mapActions(["updatePaymentMethodsSettings"]),
    initForm() {
      if (this.settings) {
        this.config = {
          gpi_x_web_id: this.settings.gpi_x_web_id,
          gpi_terminal_id: this.settings.gpi_terminal_id,
          gpi_auth_key: this.settings.gpi_auth_key,
          gpi_ach_x_web_id: this.settings.gpi_ach_x_web_id,
          gpi_ach_terminal_id: this.settings.gpi_ach_terminal_id,
          gpi_ach_auth_key: this.settings.gpi_ach_auth_key,
          gpi_enabled: this.settings.gpi_enabled,
          gpi_card_savable: this.settings.gpi_card_savable
        };
      }
    },
    handleSave() {
      if (this.isValid) {
        let params = {};
        const hasCc =
          Boolean(this.config.gpi_x_web_id) &&
          Boolean(this.config.gpi_terminal_id) &&
          Boolean(this.config.gpi_auth_key);

        if (this.modalMode === "card") {
          params = {
            ...params,
            gpi_x_web_id: this.config.gpi_x_web_id,
            gpi_terminal_id: this.config.gpi_terminal_id,
            gpi_auth_key: this.config.gpi_auth_key
          };
        }

        if (this.modalMode === "ach") {
          params = {
            ...params,
            gpi_ach_x_web_id: this.config.gpi_ach_x_web_id,
            gpi_ach_terminal_id: this.config.gpi_ach_terminal_id,
            gpi_ach_auth_key: this.config.gpi_ach_auth_key
          };
        }

        params = {
          ...params,
          gpi_card_savable: this.config.gpi_card_savable
        };

        let successMsg = "Global Payments settings saved!";

        if (!hasCc) {
          params.gpi_enabled = false;
          successMsg +=
            "<br /><br />GPI has been automatically disabled due to blank card settings.";
        }

        this.updatePaymentMethodsSettings({
          payment_methods: params
        }).then(() => {
          window.notify.success(successMsg);
          this.toggleModal();
        });
      }
    },
    handleCheckChange(field, value) {
      this.updatePaymentMethodsSettings({
        payment_methods: {
          [field]: value
        }
      })
        .then(() => {
          window.notify.success("Global Payments settings saved!");
        })
        .catch(() => {
          this.initForm();
        });
    },
    toggleModal(mode) {
      this.modalMode = mode;
      this.modalOpen = !this.modalOpen;
    }
  }
};
</script>
